// Compact environment/version row for the black sidebar, between nav and the user card.
import React, { useEffect, useState } from "react";
import { Box, IconButton, Popover } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { API_BASE_URL, APP_ENVIRONMENT, APP_VERSION, SYSTEM_NAME } from "../../config";
import { fonts, tokens } from "../../theme/tokens";
import { getRuntimeInfo, subscribe } from "../../utils/runtimeInfo";

const INACTIVE = "rgba(255,255,255,0.6)";
const HAIRLINE = "rgba(255,255,255,0.08)";

function normalizeEnvironment(value) {
  return (value || "").trim().toUpperCase();
}

function pillTone(environment) {
  const label = normalizeEnvironment(environment);
  if (label === "DEV") {
    return { label: "DEV", backgroundColor: tokens.gold, color: tokens.black };
  }
  if (label === "UAT") {
    return { label: "UAT", backgroundColor: tokens.cyan, color: tokens.black };
  }
  return {
    label: label || "—",
    backgroundColor: "rgba(255,255,255,0.12)",
    color: "rgba(255,255,255,0.85)",
  };
}

function formatAgo(lastSeenAt, now) {
  if (!lastSeenAt) return "—";
  const seconds = Math.max(0, Math.floor((now - lastSeenAt) / 1000));
  return `${seconds}s ago`;
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  return value;
}

function DetailRow({ label, value, testId }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 2,
        py: 0.4,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          color: INACTIVE,
          fontSize: 12,
          fontFamily: fonts.sans,
        }}
      >
        {label}
      </Box>
      <Box
        data-testid={testId}
        sx={{
          fontSize: 12,
          fontFamily: fonts.mono,
          color: "#fff",
          textAlign: "right",
          wordBreak: "break-all",
        }}
      >
        {displayValue(value)}
      </Box>
    </Box>
  );
}

export default function EnvironmentWidget() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [runtime, setRuntime] = useState(() => getRuntimeInfo());
  const [now, setNow] = useState(() => Date.now());
  const open = Boolean(anchorEl);

  useEffect(() => subscribe(setRuntime), []);

  useEffect(() => {
    if (!open) return undefined;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  const tone = pillTone(APP_ENVIRONMENT);
  const frontendEnvironment = normalizeEnvironment(APP_ENVIRONMENT);
  const backendEnvironment = normalizeEnvironment(runtime.environment);
  const mismatch = Boolean(backendEnvironment) && backendEnvironment !== frontendEnvironment;

  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${HAIRLINE}`,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        data-testid="environment-pill"
        sx={{
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          lineHeight: 1.4,
          px: "7px",
          py: "1px",
          borderRadius: "8px",
          backgroundColor: tone.backgroundColor,
          color: tone.color,
          fontFamily: fonts.sans,
        }}
      >
        {tone.label}
      </Box>
      <Box
        data-testid="frontend-version"
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: fonts.mono,
          fontSize: 11,
          color: INACTIVE,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {APP_VERSION}
      </Box>
      <IconButton
        size="small"
        aria-label="Runtime info"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          width: 28,
          height: 28,
          color: INACTIVE,
          "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: tokens.blackSoft,
              color: "#fff",
              border: `1px solid ${HAIRLINE}`,
              borderRadius: "8px",
              boxShadow: "none",
              minWidth: 280,
              maxWidth: 360,
              p: 1.5,
            },
          },
        }}
      >
        {mismatch ? (
          <Box
            data-testid="environment-mismatch"
            sx={{
              color: tokens.gold,
              fontFamily: fonts.sans,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              mb: 1,
            }}
          >
            Frontend {tone.label} does not match API {backendEnvironment}.
          </Box>
        ) : null}
        <DetailRow label="System name" value={SYSTEM_NAME} testId="runtime-system-name" />
        <DetailRow label="Environment" value={tone.label} testId="runtime-environment" />
        <DetailRow label="Frontend version" value={APP_VERSION} testId="runtime-frontend-version" />
        <DetailRow label="API URL" value={API_BASE_URL} testId="runtime-api-url" />
        <DetailRow label="Backend version" value={runtime.version} testId="backend-version" />
        <DetailRow label="Backend environment" value={backendEnvironment || null} testId="backend-environment" />
        <DetailRow label="Database" value={runtime.db} testId="backend-database" />
        <DetailRow label="Debug" value={runtime.debug} testId="backend-debug" />
        <DetailRow label="Server time" value={runtime.serverTime} testId="backend-server-time" />
        <DetailRow
          label="Last API response"
          value={formatAgo(runtime.lastSeenAt, now)}
          testId="last-api-response"
        />
      </Popover>
    </Box>
  );
}
